import {useState, useCallback} from 'react';

export interface UseCarsModalsReturn {
    // Modal visibility state
    showChangeVoyageModal: boolean;
    showChangeShipperConsigneeModal: boolean;
    showChangeDestinationModal: boolean;
    showSetPricingModal: boolean;
    showSetLengthPricingModal: boolean;
    showDeleteConfirm: boolean;

    // Open modal actions
    openChangeVoyageModal: () => void;
    openChangeShipperConsigneeModal: () => void;
    openChangeDestinationModal: () => void;
    openSetPricingModal: () => void;
    openSetLengthPricingModal: () => void;
    openDeleteConfirm: () => void;

    // Close modal actions
    closeChangeVoyageModal: () => void;
    closeChangeShipperConsigneeModal: () => void;
    closeChangeDestinationModal: () => void;
    closeSetPricingModal: () => void;
    closeSetLengthPricingModal: () => void;
    closeDeleteConfirm: () => void;

    // Utility
    closeAllModals: () => void;
}

/**
 * Hook to manage modal visibility state for car management views.
 * Centralizes all modal open/close logic in one place.
 */
export function useCarsModals(): UseCarsModalsReturn {
    const [showChangeVoyageModal, setShowChangeVoyageModal] = useState(false);
    const [showChangeShipperConsigneeModal, setShowChangeShipperConsigneeModal] = useState(false);
    const [showChangeDestinationModal, setShowChangeDestinationModal] = useState(false);
    const [showSetPricingModal, setShowSetPricingModal] = useState(false);
    const [showSetLengthPricingModal, setShowSetLengthPricingModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Open actions
    const openChangeVoyageModal = useCallback(() => setShowChangeVoyageModal(true), []);
    const openChangeShipperConsigneeModal = useCallback(() => setShowChangeShipperConsigneeModal(true), []);
    const openChangeDestinationModal = useCallback(() => setShowChangeDestinationModal(true), []);
    const openSetPricingModal = useCallback(() => setShowSetPricingModal(true), []);
    const openSetLengthPricingModal = useCallback(() => setShowSetLengthPricingModal(true), []);
    const openDeleteConfirm = useCallback(() => setShowDeleteConfirm(true), []);

    // Close actions
    const closeChangeVoyageModal = useCallback(() => setShowChangeVoyageModal(false), []);
    const closeChangeShipperConsigneeModal = useCallback(() => setShowChangeShipperConsigneeModal(false), []);
    const closeChangeDestinationModal = useCallback(() => setShowChangeDestinationModal(false), []);
    const closeSetPricingModal = useCallback(() => setShowSetPricingModal(false), []);
    const closeSetLengthPricingModal = useCallback(() => setShowSetLengthPricingModal(false), []);
    const closeDeleteConfirm = useCallback(() => setShowDeleteConfirm(false), []);

    // Close all modals at once
    const closeAllModals = useCallback(() => {
        setShowChangeVoyageModal(false);
        setShowChangeShipperConsigneeModal(false);
        setShowChangeDestinationModal(false);
        setShowSetPricingModal(false);
        setShowSetLengthPricingModal(false);
        setShowDeleteConfirm(false);
    }, []);

    return {
        // State
        showChangeVoyageModal,
        showChangeShipperConsigneeModal,
        showChangeDestinationModal,
        showSetPricingModal,
        showSetLengthPricingModal,
        showDeleteConfirm,

        // Open actions
        openChangeVoyageModal,
        openChangeShipperConsigneeModal,
        openChangeDestinationModal,
        openSetPricingModal,
        openSetLengthPricingModal,
        openDeleteConfirm,

        // Close actions
        closeChangeVoyageModal,
        closeChangeShipperConsigneeModal,
        closeChangeDestinationModal,
        closeSetPricingModal,
        closeSetLengthPricingModal,
        closeDeleteConfirm,

        // Utility
        closeAllModals,
    };
}
